/**
 * Featured Creators Block
 * Highlighted creator profiles carousel
 */
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, User } from 'lucide-react';
import { Badge } from '../ui/badge';
import { ScrollContainer, SectionHeader, resolveImageUrl } from './shared';

const FeaturedCreatorsBlock = ({ data = [], settings = {} }) => {
  const scrollRef = useRef(null);
  const creators = data || [];
  
  const {
    title = 'Featured Creators',
    max_items = 12,
    card_scale = 1.0,
    columns = { desktop: 6, tablet: 4, mobile: 3 },
  } = settings;

  if (creators.length === 0) return null;

  const displayCreators = creators.slice(0, max_items);

  return (
    <section className="mb-6 sm:mb-12" data-testid="featured-creators-block">
      <SectionHeader 
        title={title}
        icon={Sparkles}
        iconColor="text-purple-400"
      />
      
      <ScrollContainer scrollRef={scrollRef}>
        {displayCreators.map((creator) => (
          <Link
            key={creator.id || creator.user_id}
            to={`/creator/${creator.handle}`}
            className="flex-shrink-0 group"
            style={{ 
              width: `${Math.round(128 * card_scale)}px`,
              transform: `scale(${card_scale})`,
              transformOrigin: 'top left'
            }}
            data-testid={`featured-creator-${creator.handle}`}
          >
            <div className="relative">
              {/* Avatar */}
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500/50 group-hover:border-purple-400 transition-colors">
                {creator.avatar_url ? (
                  <img
                    src={resolveImageUrl(creator.avatar_url)}
                    alt={creator.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-12 h-12 sm:w-16 sm:h-16 text-purple-400/50" />
                  </div>
                )}
              </div>
              {/* Featured Badge */}
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-0.5 shadow-lg">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Featured
                </Badge>
              </div>
            </div>
            {/* Name & Handle */}
            <div className="mt-4 text-center">
              <p className="font-semibold text-white text-sm sm:text-base truncate group-hover:text-purple-400 transition-colors">
                {creator.display_name}
              </p>
              <p className="text-xs sm:text-sm text-zinc-500">@{creator.handle}</p>
              {/* Badges */}
              {creator.badges && creator.badges.length > 0 && (
                <div className="flex justify-center gap-1 mt-1 flex-wrap">
                  {creator.badges.slice(0, 2).map((badge) => (
                    <Badge key={badge.key} variant="outline" className="text-xs border-yellow-500/30 text-yellow-400 px-1.5 py-0">
                      {badge.label}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Link>
        ))}
      </ScrollContainer>
    </section>
  );
};

export default FeaturedCreatorsBlock;
